import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { isArchiveSearch, withArchive } from 'src/resources/withArchive';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { RootStoreType } from 'src/types/store';
import {
    allColumns,
    allColumnsSkladonUser,
    allColumnsWithProvider,
    allColumnsWithProviderSkladonUser,
    extendProductColumns,
    REQUIRED_CLAIMS_GRID_ALL_PROVIDERS_COLUMNS,
    REQUIRED_CLAIMS_GRID_COLUMNS
} from './constants';
import Table from 'src/components/Table';
import {
    ExportTypeType,
    GetAllPrintRowsType,
    OnChangeOptionsType,
    RowActionPropsType,
    SelectRowType,
    TableColumnType,
    TablePropsOptionsType,
    TableRefType,
    TableRowType
} from 'src/types/table';
import { ClaimIcon, OrderIcon } from 'src/components/SkladonIcon/Icons';
import { useDebounce } from 'src/utils/debounce';
import { getClaimsGridFetch } from 'src/resources/claimsGrid/reducer';
import { orderTypeCodes } from 'src/resources/constantsCodes/constants';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import filterUnique from 'src/utils/filterUnique';
import { CLAIM_GRID_ALL_PROVIDERS_URL, CLAIM_GRID_PRODUCTS_URL, CLAIM_GRID_URL } from 'src/resources/claimsGrid/sagas';
import api from '@api';
import { ClaimType } from 'src/types/claim';
import { useCheckPermission } from 'src/utils/checkPermission';
import checkOrChangeProvider from 'src/utils/checkOrChangeProvider';
import { ConstantsCodesObjectType, ConstantsCodeType } from 'src/types/constantsCodes';
import { disabbleMessages } from 'src/resources/alertMessages/reducer';
import getStatusColor from 'src/utils/getStatusColor';
import colorOptionFce from 'src/utils/colorOptionFce';
import ConfirmDialog, { ConfirmDialogStatePropsType } from '../../components/ConfirmDialog';
import { patchMultipleClaimsFetch } from '../../resources/claimsUpdate/reducer';

export const SESSION_NAME = 'claimsTable';

const customRender = (
    column: TableColumnType,
    row: TableRowType,
    statusCodesObject: ConstantsCodesObjectType
): ReactNode | string => {
    switch (column.key) {
        case 'STATUS':
            return (
                <span style={{ color: getStatusColor(statusCodesObject[row.STATUS]?.color) }}>
                    {statusCodesObject[row.STATUS]?.label}
                </span>
            );
        default:
            return row[column.key];
    }
};

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'ID', direction: 'DESC' },
    page: 1,
    items: 20,
    columns: []
};
const AllClaimsContainer: FC = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const tableRef = useRef<TableRefType>(null);

    const sessionOptions = JSON.parse(sessionStorage.getItem(SESSION_NAME) || 'null');

    const [allProviders, setAllProviders] = useState<boolean>(false);
    const [archiveEnabled, setArchiveEnabled] = useState<boolean>(isArchiveSearch(location.search));
    const [formatedData, setFormatedData] = useState<ClaimType[]>([]);
    const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(sessionOptions || initTableOptions);

    const loading = useSelector((state: RootStoreType) => state.claimsGrid.loading);
    const claimsData = useSelector((state: RootStoreType) => state.claimsGrid.data);

    const claimStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.claimStatusCodes);
    const claimStatusCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.claimStatusCodes);
    const shipperCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.shipperCodes);
    const claimsResolverCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.claimsResolverCodes);
    const claimReasonCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.claimReasonCodes);
    const currencyCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.currencyCodes);
    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);
    const providers = useSelector((state: RootStoreType) => state.user.data?.providers);
    const provider = useSelector((state: RootStoreType) => state.provider.data);
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);
    const [selectedRows, setSelectedRows] = useState<SelectRowType[]>([]);
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const claimErrorDepartmentCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.claimErrorDepartmentCodes
    );
    const claimErrorCategoryCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.claimErrorCategoryCodes
    );
    const claimErrorSeverityCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.claimErrorSeverityCodes
    );
    const skladonUser = useSelector((state: RootStoreType) => state.user.data?.SKLADON_USER);

    let tableColumns: TableColumnType[];

    if (skladonUser && allProviders) {
        tableColumns = allColumnsWithProviderSkladonUser;
    } else if (skladonUser && !allProviders) {
        tableColumns = allColumnsSkladonUser;
    } else if (!skladonUser && allProviders) {
        tableColumns = allColumnsWithProvider;
    } else {
        tableColumns = allColumns;
    }

    // allow selection of rows whre edit permiision exists for selected claim status
    // if (selectedRows?.length > 0 && claimsData) {
    //     console.log('selectedRows', selectedRows);
    //     const newSelectedRows: string[] = [];
    //     claimsData.data
    //         .filter((item) => selectedRows.includes(String(item.ID)))
    //         .forEach((item) => {
    //             if (item.STATUS && checkPermission(`CLAIM.STATUS.EDIT.${item.STATUS.toUpperCase()}`)) {
    //                 newSelectedRows.push(String(item.ID));
    //             }
    //         });
    //
    //     setSelectedRows(newSelectedRows);
    // }
    const permissionAllProviders = useCheckPermission('CLAIM.LISTALLPROVIDERS');
    const permissionArchive = useCheckPermission('ARCHIVE.READ');
    const archive = archiveEnabled && permissionArchive;
    const permissionClaimDetail = useCheckPermission('CLAIM.DETAIL');
    const permissionOrderDetail = useCheckPermission('ORDER.DETAIL');
    const permissionClaimUpdate = useCheckPermission('CLAIM.UPDATE');
    const permissionClaimImport = useCheckPermission('CLAIM.IMPORT');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('claims.title') }));
    }, []);

    const debouncedTableOptions = useDebounce(tableOptions, 500);

    useEffect(() => {
        loadTableData();
    }, [debouncedTableOptions, archive]);

    useEffect(() => {
        if (claimsData?.data) {
            setFormatedData(getFormatedData(claimsData?.data || []));
        }
    }, [claimsData?.data]);

    const getFormatedData = (data: ClaimType[]): ClaimType[] => {
        if (!allProviders) {
            return data;
        }
        return data.map((item) => {
            return getFormatedRow(item);
        });
    };

    const getFormatedRow = (row: TableRowType): TableRowType => {
        const provider = providers?.find((provider) => provider.ESHOP_KEY === row.ESHOP_KEY);
        return { ...row, providerName: provider?.COMPANY_NAME };
    };

    const getAllGridParams = () => ({
        filters: [[...(tableOptions.filters || [])]],
        sorts: [tableOptions.sort],
        page: tableOptions.page,
        items: tableOptions.items,
        columns: [
            ...(allProviders ? REQUIRED_CLAIMS_GRID_ALL_PROVIDERS_COLUMNS : REQUIRED_CLAIMS_GRID_COLUMNS),
            ...tableOptions.columns.filter((column) => !column.allwaysShow).map((column) => column.key)
        ].filter(filterUnique)
    });

    const loadTableData = () => {
        if (tableOptions.columns.length) {
            dispatch(getClaimsGridFetch({ params: getAllGridParams(), allProviders, archive }));
        }
    };

    const handleChangeOptions: OnChangeOptionsType = (newOptions) => {
        setTableOptions(newOptions);
    };

    const getAllRows: GetAllPrintRowsType = async (extendParams) => {
        const params = getAllGridParams();
        const res = await api('post', withArchive(allProviders ? CLAIM_GRID_ALL_PROVIDERS_URL : CLAIM_GRID_URL, archive), {
            ...params,
            ...extendParams
        });
        return getFormatedData(res.data.data.data);
    };

    const handleExport = (type: ExportTypeType, withProducts?: boolean) => {
        tableRef.current?.exportTableGrid(
            type,
            translate(withProducts ? 'claims.action.exportWithProductsFileName' : 'claims.action.exportFileName'),
            getAllGridParams,
            allProviders ? CLAIM_GRID_ALL_PROVIDERS_URL : withProducts ? CLAIM_GRID_PRODUCTS_URL : CLAIM_GRID_URL,
            withProducts ? extendProductColumns : undefined,
            { rowFunction: allProviders ? getFormatedRow : undefined }
        );
    };

    const handleMassChangeStatus = (statuses: ConstantsCodeType[]) => {
        setConfirmParams({
            title: translate('claims.action.changeStatuses'),
            content: translate('claims.action.changeStatusesText', { count: selectedRows.length }),
            options: statuses,
            optionsValue: statuses[0].value,
            action: (value: ExportTypeType) => {
                dispatch(
                    patchMultipleClaimsFetch({
                        claims: claimsData?.data.filter((claim) => selectedRows.includes(String(claim.ID))),
                        data: { STATUS: value }
                    })
                );
            }
        });
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const changeProvider = (row: TableRowType) => {
        if (allProviders && providers && provider) {
            if (checkOrChangeProvider(providers, provider, 'ESHOP_KEY', row.ESHOP_KEY)) {
                dispatch(disabbleMessages());
            }
        }
    };

    const handleArchive = (value: boolean) => {
        setSelectedRows([]);
        if (value) {
            setAllProviders(false);
        }
        setArchiveEnabled(value);
    };

    const reloadTableData = () => {
        dispatch(getClaimsGridFetch(undefined));
    };

    const tableRowActions: RowActionPropsType[] = [
        {
            icon: <ClaimIcon />,
            label: translate('claims.action.claimDetail'),
            getLink: (row) => withArchive(`/claims/${row.ID}/`, archive),
            onClick: changeProvider,
            hiddenFce: () => !permissionClaimDetail
        },
        {
            icon: <OrderIcon />,
            label: translate('claims.action.orderDetail'),
            getLink: (row) => withArchive(`/orders/${row.ORDER_ID}/`, archive),
            onClick: changeProvider,
            hiddenFce: (row) => !row.ORDER_ID || !permissionOrderDetail
        }
    ];

    const tableSelectOptions: {
        [key: string]: ConstantsCodeType[];
    } = {
        claimStatusCodes,
        shipperCodes,
        orderTypeCodes,
        claimsResolverCodes,
        boolNumberCodes,
        claimReasonCodes,
        currencyCodes,
        claimErrorDepartmentCodes,
        claimErrorCategoryCodes,
        claimErrorSeverityCodes
    };

    return (
        <>
            <TopBlock
                handleExport={handleExport}
                allProviders={allProviders}
                setAllProviders={setAllProviders}
                archive={archive}
                permissionArchive={permissionArchive}
                setArchive={handleArchive}
                reloadTableData={reloadTableData}
                permissionAllProviders={permissionAllProviders}
                claimStatusCodesObject={claimStatusCodesObject}
                claimStatusCodes={claimStatusCodes}
                selectedRows={selectedRows}
                handleMassChangeStatus={handleMassChangeStatus}
                permissionClaimUpdate={permissionClaimUpdate}
                permissionClaimImport={permissionClaimImport}
                claimsDataData={claimsData?.data}
            />
            <div data-testid="claims-list">
                <Table
                    allColumns={tableColumns}
                    ref={tableRef}
                    rowIdKey="ID"
                    rows={formatedData}
                    loading={loading}
                    columsLabelPrefix="claims.table"
                    options={tableOptions}
                    totalCount={claimsData?.totalCount}
                    onChangeOptions={handleChangeOptions}
                    rowActions={tableRowActions}
                    selectOptions={tableSelectOptions}
                    userSettingsName="showClaimsTableColumns"
                    userSettingsData={userSettingsData}
                    editUserSettings={editUserSettings}
                    optionsSessionName={SESSION_NAME}
                    selectedRows={!archive ? selectedRows : undefined}
                    setSelectedRows={!archive ? setSelectedRows : undefined}
                    fullHeight
                    customRender={(column, row) => customRender(column, row, claimStatusCodesObject)}
                    optionStyleFce={colorOptionFce}
                />
            </div>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default AllClaimsContainer;
